import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import config from "../coreFIles/config";
import { adminInfoVideoDeleteAction, adminInfoVideosListAction } from "../Action/action";
import DeleteConfirmModal from "./delete-confirm-modal";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

export default function InfoVideos() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminInfoVideosListAction();
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setError(res?.message || res?.msg || "Failed to load info videos.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setError(e?.response?.data?.message || e?.message || "Failed to load info videos.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadNonce]);

  const cards = useMemo(() => {
    return (Array.isArray(rows) ? rows : []).map((v) => {
      const id = v?.id;
      const title = v?.title || "Video";
      const type = v?.type || "Selfhosted";
      const active = v?.active !== undefined && v?.active !== null ? !!Number(v.active) : true;
      const videoUrl = v?.video_url ? String(v.video_url) : "";
      const thumbUrl = v?.thumbnail_url ? String(v.thumbnail_url) : "";
      return {
        raw: v,
        id,
        title,
        type,
        active,
        videoUrl,
        videoHref: type === "Thirdparty" ? videoUrl : resolvePublicUrl(videoUrl),
        thumbUrl,
        thumbPublic: resolvePublicUrl(thumbUrl),
        created: formatDate(v?.created_at)
      };
    });
  }, [rows]);

  const openVideo = (c) => {
    if (!c?.videoHref) {
      toast.error("Video URL not available", { toastId: "info-video-open" });
      return;
    }
    window.open(c.videoHref, "_blank", "noopener,noreferrer");
  };

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminInfoVideoDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Delete failed");
      toast.success("Info video deleted", { toastId: "info-video-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error("Delete failed", { toastId: "info-video-delete" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Info Videos unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <h3 style={{ margin: 0 }}>Info Videos</h3>
        </div>
        <Link to="/info-videos/new" className="btn btn-primary">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
          Add Info Video
        </Link>
      </div>

      <div className="card">
        <div className="settings-section">
          <h3 style={{ marginBottom: 8 }}>Videos</h3>
          <p className="help-text">Manage informational videos shown in the app.</p>

          {loading ? (
            <div className="empty-state" style={{ padding: "28px 12px" }}>
              Loading…
            </div>
          ) : cards.length === 0 ? (
            <div className="empty-state" style={{ padding: "28px 12px" }}>
              <div className="empty-icon">🎬</div>
              No info videos yet.
            </div>
          ) : (
            <div className="video-grid">
              {cards.map((c) => (
                <div className="video-grid-item" key={c.id}>
                  <div
                    className="video-grid-thumb video-grid-thumb-lg clickable"
                    onClick={() => openVideo(c)}
                    style={
                      c.thumbPublic
                        ? {
                            backgroundImage: `url("${c.thumbPublic}")`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            color: "rgba(255,255,255,0.92)"
                          }
                        : undefined
                    }
                    title="Open video"
                  >
                    ▶️
                  </div>
                  <div className="video-grid-info">
                    <h4>{c.title}</h4>
                    <p>
                      <span className="badge badge-info badge-sm">{c.type}</span>{" "}
                      <span className={`badge ${c.active ? "badge-success" : "badge-gray"} badge-sm`}>{c.active ? "Active" : "Inactive"}</span>{" "}
                      <span className="small-text">· {c.created}</span>
                    </p>
                    <div className="video-grid-actions">
                      <Link className="btn btn-outline btn-sm" to={`/info-videos/${c.id}/edit`}>
                        ✏️ Edit
                      </Link>
                      <button className="btn btn-outline btn-sm text-danger" type="button" onClick={() => openDelete(c)}>
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="info video"
        itemName={deleteRow?.title || "Info Video"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}

